/* ═══════════════════════════════════════════════════════════════
   DataTable — Reusable data table with sort, filter, search,
   group, column reorder, column resize, grid edit mode (SharePoint-
   style two-state: selected vs editing), copy/paste cells, multi-
   cell selection, validated combobox dropdowns, keyboard navigation,
   drag-to-fill, and text size slider.
   ═══════════════════════════════════════════════════════════════ */

import React, { useState, useRef, useMemo, useCallback, useEffect } from 'react';
import { SearchableSelect } from './SearchableSelect';
import { Prefs } from '../services/prefsService';

// ─── Types ───

export interface Column<T> {
  key: string;
  label: string;
  width?: number; // initial width in px
  minWidth?: number; // minimum resize width (default 50)
  sortable?: boolean; // default true
  render?: (item: T) => React.ReactNode;
  getValue?: (item: T) => string | number;
  // Grid-edit mode rendering (shown when grid editing is on)
  editable?: boolean;
  editType?: 'text' | 'select' | 'checkbox' | 'date';
  editOptions?: { value: string; label: string }[];
  editKey?: string; // actual field key if different from display key
  // Custom cell render for grid-edit mode (overrides editType)
  renderEdit?: (item: T) => React.ReactNode;
}

export interface GroupOption<T> {
  key: string;
  label: string;
  getValue: (item: T) => string;
}

export interface DataTableProps<T> {
  columns: Column<T>[];
  data: T[];
  keyField: keyof T & string;
  onRowClick?: (item: T) => void;
  actions?: (item: T) => React.ReactNode;
  groupOptions?: GroupOption<T>[];
  searchPlaceholder?: string;
  initialSort?: { key: string; direction: 'asc' | 'desc' };
  emptyIcon?: string;
  emptyTitle?: string;
  emptyMessage?: string;
  toolbarExtra?: React.ReactNode;
  // Grid editing (SharePoint-style)
  onCellEdit?: (item: T, key: string, value: any) => void;
  // Paste from clipboard (Excel / CSV) — creates new rows
  onPaste?: (rows: Record<string, string>[]) => void;
  // Blank "add" row at bottom of grid — returns field values to create
  onNewRow?: (values: Record<string, string>) => void;
  // Persist column widths and order to localStorage under this key
  storageKey?: string;
}

// ─── Cell reference for selection ───

interface CellRef {
  row: number; // flat index across all groups
  col: string; // column key
}

// ─── ValidatedCombobox — type-to-filter dropdown with validation ───

function ValidatedCombobox({
  value,
  options,
  onChange,
  fontSize,
  autoFocus,
  onExit,
}: {
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
  fontSize: number;
  autoFocus?: boolean;
  onExit?: (dir: 'tab' | 'shift-tab' | 'enter' | 'escape') => void;
}) {
  const currentLabel = options.find((o) => o.value === value)?.label || '';
  const [text, setText] = useState(currentLabel);
  const [open, setOpen] = useState(autoFocus || false);
  const [highlightIdx, setHighlightIdx] = useState(-1);
  const ref = useRef<HTMLInputElement>(null);
  const dropRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setText(options.find((o) => o.value === value)?.label || '');
  }, [value, options]);

  useEffect(() => {
    if (autoFocus) ref.current?.focus();
  }, [autoFocus]);

  const filtered = useMemo(() => {
    if (!text) return options;
    const q = text.toLowerCase();
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [text, options]);

  function commit(val: string) {
    const match = options.find((o) => o.value === val);
    if (match) {
      onChange(match.value);
      setText(match.label);
    } else {
      setText(options.find((o) => o.value === value)?.label || '');
    }
    setOpen(false);
    setHighlightIdx(-1);
  }

  function handleBlur() {
    setTimeout(() => {
      if (dropRef.current?.contains(document.activeElement)) return;
      const match = options.find((o) => o.label.toLowerCase() === text.toLowerCase());
      commit(match ? match.value : value);
    }, 150);
  }

  return (
    <div style={{ position: 'relative' }}>
      <input
        ref={ref}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          setOpen(true);
          setHighlightIdx(0);
        }}
        onFocus={() => {
          setOpen(true);
          setHighlightIdx(-1);
        }}
        onBlur={handleBlur}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            e.stopPropagation();
            if (!open) {
              setOpen(true);
              setHighlightIdx(0);
            } else setHighlightIdx((prev) => Math.min(prev + 1, filtered.length - 1));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            e.stopPropagation();
            setHighlightIdx((prev) => Math.max(prev - 1, 0));
          } else if (e.key === 'Enter') {
            e.preventDefault();
            e.stopPropagation();
            if (open && highlightIdx >= 0 && highlightIdx < filtered.length) {
              commit(filtered[highlightIdx].value);
            } else {
              const match = options.find((o) => o.label.toLowerCase() === text.toLowerCase());
              commit(match ? match.value : value);
            }
            onExit?.('enter');
          } else if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            setText(options.find((o) => o.value === value)?.label || '');
            setOpen(false);
            onExit?.('escape');
          } else if (e.key === 'Tab') {
            e.preventDefault();
            e.stopPropagation();
            const match = options.find((o) => o.label.toLowerCase() === text.toLowerCase());
            commit(match ? match.value : value);
            onExit?.(e.shiftKey ? 'shift-tab' : 'tab');
          }
        }}
        style={{ ...gridInputStyle, fontSize }}
        placeholder="Type to search..."
        onClick={(e) => e.stopPropagation()}
        onDoubleClick={(e) => e.stopPropagation()}
      />
      {open && filtered.length > 0 && (
        <div
          ref={dropRef}
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            maxHeight: 200,
            overflowY: 'auto',
            background: 'var(--sf)',
            border: '1px solid var(--bd)',
            borderRadius: '0 0 4px 4px',
            boxShadow: '0 4px 12px rgba(0,0,0,.15)',
            zIndex: 100,
          }}
        >
          {filtered.map((o, i) => (
            <div
              key={o.value}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                commit(o.value);
                onExit?.('enter');
              }}
              onMouseEnter={() => setHighlightIdx(i)}
              style={{
                padding: '5px 8px',
                fontSize: fontSize - 1,
                cursor: 'pointer',
                background:
                  i === highlightIdx
                    ? 'var(--ac)'
                    : o.value === value
                      ? 'var(--sa)'
                      : 'transparent',
                color: i === highlightIdx ? 'var(--color-text-inverse)' : 'var(--tx)',
              }}
            >
              {o.label}
            </div>
          ))}
        </div>
      )}
      {open && filtered.length === 0 && text && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            padding: '6px 8px',
            fontSize: fontSize - 2,
            color: 'var(--tm)',
            background: 'var(--sf)',
            border: '1px solid var(--bd)',
            borderRadius: '0 0 4px 4px',
            fontStyle: 'italic',
            zIndex: 100,
          }}
        >
          No match
        </div>
      )}
    </div>
  );
}

// ─── Component ───

export function DataTable<T>({
  columns: initialColumns,
  data,
  keyField,
  onRowClick,
  actions,
  groupOptions,
  searchPlaceholder = 'Search...',
  initialSort,
  emptyIcon = '\uD83D\uDCC4',
  emptyTitle = 'No Data',
  emptyMessage = 'Nothing to show yet.',
  toolbarExtra,
  onCellEdit,
  onPaste,
  onNewRow,
  storageKey,
}: DataTableProps<T>) {
  // ─── State ───
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState(() => {
    if (storageKey) {
      try {
        const s = localStorage.getItem(`dt_sortKey_${storageKey}`);
        if (s) return s;
      } catch {}
    }
    return initialSort?.key || initialColumns[0]?.key || '';
  });
  const [sortAsc, setSortAsc] = useState(() => {
    if (storageKey) {
      try {
        const s = localStorage.getItem(`dt_sortAsc_${storageKey}`);
        if (s) return s === 'true';
      } catch {}
    }
    return initialSort?.direction !== 'desc';
  });
  const [groupKey, setGroupKey] = useState(() => {
    if (storageKey) {
      try {
        const s = localStorage.getItem(`dt_groupKey_${storageKey}`);
        if (s) return s;
      } catch {}
    }
    return 'none';
  });
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(() => {
    if (storageKey) {
      try {
        const s = localStorage.getItem(`dt_collapsed_${storageKey}`);
        if (s) return new Set(JSON.parse(s));
      } catch {}
    }
    return new Set();
  });
  const [fontSize, setFontSize] = useState(() => {
    if (storageKey) {
      try {
        const s = localStorage.getItem(`dt_fontSize_${storageKey}`);
        if (s) return Number(s);
      } catch {}
    }
    return 13;
  });
  const [gridEditMode, setGridEditMode] = useState(false);

  // ─── Two-state cell model: selected vs editing ───
  const [focusCell, setFocusCell] = useState<CellRef | null>(null);
  const [selectionEnd, setSelectionEnd] = useState<CellRef | null>(null);
  const [editingCell, setEditingCell] = useState<CellRef | null>(null);
  // When user types on a selected cell, pre-fill the input with that character
  const pendingChar = useRef<string | null>(null);

  // Clear selection when leaving grid edit mode
  useEffect(() => {
    if (!gridEditMode) {
      setFocusCell(null);
      setSelectionEnd(null);
      setEditingCell(null);
    }
  }, [gridEditMode]);

  // Column order (array of keys) — persisted if storageKey provided
  const [columnOrder, setColumnOrder] = useState<string[]>(() => {
    if (storageKey) {
      try {
        const s = localStorage.getItem(`dt_order_${storageKey}`);
        if (s) return JSON.parse(s);
      } catch {}
    }
    return initialColumns.map((c) => c.key);
  });
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(() => {
    if (storageKey) {
      try {
        const s = localStorage.getItem(`dt_widths_${storageKey}`);
        if (s) return JSON.parse(s);
      } catch {}
    }
    const w: Record<string, number> = {};
    for (const c of initialColumns) w[c.key] = c.width || 150;
    return w;
  });

  useEffect(() => {
    setColumnOrder((prev) => {
      const newKeys = initialColumns.map((c) => c.key);
      const existing = prev.filter((k) => newKeys.includes(k));
      const added = newKeys.filter((k) => !prev.includes(k));
      return [...existing, ...added];
    });
  }, [initialColumns.map((c) => c.key).join(',')]);

  // ── Per-user server sync: the user's saved layout (user_prefs) wins over
  // localStorage once loaded, so tables look the same on every device and on
  // web/desktop alike. Applied ONCE per mount, before any local resize.
  const prefsAppliedRef = useRef(false);
  const [prefsTick, setPrefsTick] = useState(0);
  useEffect(() => Prefs.subscribe(() => setPrefsTick((v) => v + 1)), []);
  useEffect(() => {
    if (prefsAppliedRef.current || !storageKey || !Prefs.isReady()) return;
    prefsAppliedRef.current = true;
    const w = Prefs.get(`dt_widths_${storageKey}`);
    if (w && typeof w === 'object') setColumnWidths((cur) => ({ ...cur, ...w }));
    const o = Prefs.get(`dt_order_${storageKey}`);
    if (Array.isArray(o) && o.length > 0) {
      setColumnOrder(() => {
        const newKeys = initialColumns.map((c) => c.key);
        const existing = o.filter((k: string) => newKeys.includes(k));
        const added = newKeys.filter((k) => !existing.includes(k));
        return [...existing, ...added];
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefsTick, storageKey]);

  // Persist column widths to localStorage (debounced to avoid thrashing during
  // resize) + the per-user server copy.
  const widthsTimer = useRef<any>(null);
  useEffect(() => {
    if (!storageKey) return;
    if (widthsTimer.current) clearTimeout(widthsTimer.current);
    widthsTimer.current = setTimeout(() => {
      try {
        localStorage.setItem(`dt_widths_${storageKey}`, JSON.stringify(columnWidths));
      } catch {}
      if (prefsAppliedRef.current) Prefs.set(`dt_widths_${storageKey}`, columnWidths);
    }, 300);
  }, [columnWidths, storageKey]);
  useEffect(() => {
    if (storageKey) {
      try {
        localStorage.setItem(`dt_order_${storageKey}`, JSON.stringify(columnOrder));
      } catch {}
      if (prefsAppliedRef.current) Prefs.set(`dt_order_${storageKey}`, columnOrder);
    }
  }, [columnOrder, storageKey]);
  useEffect(() => {
    if (storageKey)
      try {
        localStorage.setItem(`dt_fontSize_${storageKey}`, String(fontSize));
      } catch {}
  }, [fontSize, storageKey]);
  useEffect(() => {
    if (storageKey)
      try {
        localStorage.setItem(`dt_collapsed_${storageKey}`, JSON.stringify([...collapsedGroups]));
      } catch {}
  }, [collapsedGroups, storageKey]);
  useEffect(() => {
    if (storageKey)
      try {
        localStorage.setItem(`dt_sortKey_${storageKey}`, sortKey);
      } catch {}
  }, [sortKey, storageKey]);
  useEffect(() => {
    if (storageKey)
      try {
        localStorage.setItem(`dt_sortAsc_${storageKey}`, String(sortAsc));
      } catch {}
  }, [sortAsc, storageKey]);
  useEffect(() => {
    if (storageKey)
      try {
        localStorage.setItem(`dt_groupKey_${storageKey}`, groupKey);
      } catch {}
  }, [groupKey, storageKey]);

  // Hidden columns — persisted set of column keys the user chose to hide
  const [hiddenColumns, setHiddenColumns] = useState<Set<string>>(() => {
    if (storageKey) {
      try {
        const s = localStorage.getItem(`dt_hidden_${storageKey}`);
        if (s) return new Set(JSON.parse(s));
      } catch {}
    }
    return new Set();
  });
  useEffect(() => {
    if (storageKey)
      try {
        localStorage.setItem(`dt_hidden_${storageKey}`, JSON.stringify([...hiddenColumns]));
      } catch {}
  }, [hiddenColumns, storageKey]);

  // Column visibility context menu
  const [colMenuPos, setColMenuPos] = useState<{ x: number; y: number } | null>(null);

  const allOrderedColumns = useMemo(() => {
    const colMap = new Map(initialColumns.map((c) => [c.key, c]));
    return columnOrder.map((k) => colMap.get(k)).filter(Boolean) as Column<T>[];
  }, [columnOrder, initialColumns]);

  const orderedColumns = useMemo(() => {
    return allOrderedColumns.filter((c) => !hiddenColumns.has(c.key));
  }, [allOrderedColumns, hiddenColumns]);

  // Editable columns that participate in selection (have editable:true)
  const editableCols = useMemo(
    () => orderedColumns.filter((c) => c.editable).map((c) => c.key),
    [orderedColumns]
  );

  const hasEditable = initialColumns.some((c) => c.editable || c.renderEdit);

  // ─── Search / Sort / Group ───

  const getColValue = useCallback((item: T, col: Column<T>): string | number => {
    if (col.getValue) return col.getValue(item);
    const v = (item as any)[col.key];
    return v == null ? '' : String(v);
  }, []);

  const filtered = useMemo(() => {
    let list = data;
    if (search) {
      const q = search.toLowerCase();
      list = list.filter((item) =>
        initialColumns.some((col) => {
          const v = getColValue(item, col);
          return String(v).toLowerCase().includes(q);
        })
      );
    }
    const sortCol = initialColumns.find((c) => c.key === sortKey);
    if (sortCol && sortCol.sortable !== false) {
      list = [...list].sort((a, b) => {
        const va = getColValue(a, sortCol);
        const vb = getColValue(b, sortCol);
        if (typeof va === 'number' && typeof vb === 'number') return va - vb;
        const sa = String(va).toLowerCase(),
          sb = String(vb).toLowerCase();
        if (sa < sb) return -1;
        if (sa > sb) return 1;
        return 0;
      });
      if (!sortAsc) list = list.reverse();
    }
    return list;
  }, [data, search, sortKey, sortAsc, initialColumns, getColValue]);

  const grouped = useMemo(() => {
    if (groupKey === 'none' || !groupOptions) return [{ label: '', items: filtered }];
    const opt = groupOptions.find((g) => g.key === groupKey);
    if (!opt) return [{ label: '', items: filtered }];
    const map = new Map<string, T[]>();
    for (const item of filtered) {
      const g = opt.getValue(item) || 'Ungrouped';
      if (!map.has(g)) map.set(g, []);
      map.get(g)!.push(item);
    }
    return Array.from(map.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([label, items]) => ({ label, items }));
  }, [filtered, groupKey, groupOptions]);

  // Flat items for selection indexing
  const flatItems = useMemo(() => grouped.flatMap((g) => g.items), [grouped]);

  // ─── Selection helpers ───

  function getSelectionRect() {
    if (!focusCell) return null;
    const end = selectionEnd || focusCell;
    const ci1 = editableCols.indexOf(focusCell.col);
    const ci2 = editableCols.indexOf(end.col);
    if (ci1 === -1 || ci2 === -1) return null;
    return {
      minRow: Math.min(focusCell.row, end.row),
      maxRow: Math.max(focusCell.row, end.row),
      minCol: Math.min(ci1, ci2),
      maxCol: Math.max(ci1, ci2),
    };
  }

  function isCellInSelection(rowIdx: number, colKey: string): boolean {
    const rect = getSelectionRect();
    if (!rect) return false;
    const ci = editableCols.indexOf(colKey);
    if (ci === -1) return false;
    return rowIdx >= rect.minRow && rowIdx <= rect.maxRow && ci >= rect.minCol && ci <= rect.maxCol;
  }

  function isCellFocused(rowIdx: number, colKey: string): boolean {
    return focusCell?.row === rowIdx && focusCell?.col === colKey;
  }

  function isCellEditing(rowIdx: number, colKey: string): boolean {
    return editingCell?.row === rowIdx && editingCell?.col === colKey;
  }

  // Get display text for a cell (used in copy and for selected-state rendering)
  function getCellDisplayText(item: T, col: Column<T>): string {
    const editKey = col.editKey || col.key;
    const raw = (item as any)[editKey];
    if (col.editType === 'select' && col.editOptions) {
      return col.editOptions.find((o) => o.value === raw)?.label || '';
    }
    if (col.editType === 'checkbox') return raw ? 'Yes' : 'No';
    return String(raw == null ? '' : raw);
  }

  // Apply a value to a cell (used in paste and delete)
  function applyValueToCell(rowIdx: number, colIdx: number, value: string) {
    if (!onCellEdit) return;
    const item = flatItems[rowIdx];
    if (!item) return;
    const colKey = editableCols[colIdx];
    const col = initialColumns.find((c) => c.key === colKey);
    if (!col || !col.editable) return;
    const editKey = col.editKey || col.key;

    if (col.editType === 'select' && col.editOptions) {
      const match = col.editOptions.find(
        (o) => o.label.toLowerCase() === value.toLowerCase() || o.value === value
      );
      if (match) onCellEdit(item, editKey, match.value);
      // Skip invalid values silently
    } else if (col.editType === 'checkbox') {
      const boolVal = ['true', '1', 'yes', 'x', '\u2713'].includes(value.toLowerCase());
      onCellEdit(item, editKey, boolVal);
    } else {
      onCellEdit(item, editKey, value);
    }
  }

  // ─── Edit mode helpers ───

  function enterEditMode(cell: CellRef) {
    const col = initialColumns.find((c) => c.key === cell.col);
    if (!col?.editable) return;
    // Checkbox: just toggle on enter/click — no separate edit mode
    if (col.editType === 'checkbox') {
      const item = flatItems[cell.row];
      if (item && onCellEdit) {
        const editKey = col.editKey || col.key;
        onCellEdit(item, editKey, !(item as any)[editKey]);
      }
      return;
    }
    setEditingCell(cell);
  }

  function exitEditMode(direction?: 'down' | 'tab' | 'shift-tab' | 'stay') {
    setEditingCell(null);
    pendingChar.current = null;
    if (!focusCell) return;

    const ci = editableCols.indexOf(focusCell.col);
    let newRow = focusCell.row;
    let newCol = ci;

    if (direction === 'down') {
      newRow = Math.min(flatItems.length - 1, newRow + 1);
    } else if (direction === 'tab') {
      newCol++;
      if (newCol >= editableCols.length) {
        newCol = 0;
        newRow = Math.min(flatItems.length - 1, newRow + 1);
      }
    } else if (direction === 'shift-tab') {
      newCol--;
      if (newCol < 0) {
        newCol = editableCols.length - 1;
        newRow = Math.max(0, newRow - 1);
      }
    }

    setFocusCell({ row: newRow, col: editableCols[newCol] });
    setSelectionEnd(null);

    // Refocus table container so keyboard events work
    setTimeout(() => tableRef.current?.focus(), 0);
  }

  // ─── Column Drag Reorder ───
  const dragColRef = useRef<string | null>(null);
  const [dragOverCol, setDragOverCol] = useState<string | null>(null);

  function handleColDragStart(e: React.DragEvent, key: string) {
    dragColRef.current = key;
    e.dataTransfer.effectAllowed = 'move';
    try {
      const el = document.createElement('div');
      el.style.cssText = 'position:fixed;top:-9999px;opacity:0;pointer-events:none';
      document.body.appendChild(el);
      e.dataTransfer.setDragImage(el, 0, 0);
      requestAnimationFrame(() => {
        try {
          document.body.removeChild(el);
        } catch {}
      });
    } catch {}
  }

  function handleColDragOver(e: React.DragEvent, key: string) {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverCol(key);
  }

  function handleColDrop(e: React.DragEvent, key: string) {
    e.preventDefault();
    setDragOverCol(null);
    const from = dragColRef.current;
    if (!from || from === key) return;
    setColumnOrder((prev) => {
      const arr = [...prev];
      const fi = arr.indexOf(from);
      const ti = arr.indexOf(key);
      if (fi === -1 || ti === -1) return arr;
      arr.splice(fi, 1);
      arr.splice(ti, 0, from);
      return arr;
    });
  }

  // ─── Column Resize ───
  const resizeRef = useRef<{ key: string; startX: number; startWidth: number } | null>(null);
  // Track whether a drag/resize just ended — suppress next click to prevent sort/nav triggers
  const suppressClickRef = useRef(false);
  // Track mousedown target to prevent click-on-release from triggering wrong actions
  const mouseDownTargetRef = useRef<EventTarget | null>(null);

  const isResizing = useRef(false);

  function handleResizeStart(e: React.MouseEvent, key: string) {
    e.preventDefault();
    e.stopPropagation();
    isResizing.current = true;
    const startX = e.clientX;
    const startWidth = columnWidths[key] || 150;
    const minW = initialColumns.find((c) => c.key === key)?.minWidth || 50;
    let rafId = 0;
    let lastWidth = startWidth;
    const handleMove = (ev: MouseEvent) => {
      ev.preventDefault();
      const delta = ev.clientX - startX;
      lastWidth = Math.max(minW, startWidth + delta);
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        setColumnWidths((prev) => ({ ...prev, [key]: lastWidth }));
      });
    };
    const handleUp = () => {
      isResizing.current = false;
      if (rafId) cancelAnimationFrame(rafId);
      setColumnWidths((prev) => ({ ...prev, [key]: lastWidth }));
      suppressClickRef.current = true;
      setTimeout(() => {
        suppressClickRef.current = false;
      }, 0);
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
  }

  // ─── Sort Toggle ───
  function toggleSort(key: string) {
    if (suppressClickRef.current) return;
    if (sortKey === key) setSortAsc(!sortAsc);
    else {
      setSortKey(key);
      setSortAsc(true);
    }
  }
  const sortArrow = (key: string) => (sortKey === key ? (sortAsc ? ' \u25B2' : ' \u25BC') : '');

  // ─── Cell mouse handlers (grid edit mode) ───

  function handleCellMouseDown(e: React.MouseEvent, rowIdx: number, colKey: string) {
    if (!gridEditMode) return;
    if (!editableCols.includes(colKey)) return;

    // If clicking a different cell while editing, exit edit mode first
    if (editingCell && !(editingCell.row === rowIdx && editingCell.col === colKey)) {
      setEditingCell(null);
    }

    if (e.shiftKey && focusCell) {
      // Extend selection
      e.preventDefault();
      setSelectionEnd({ row: rowIdx, col: colKey });
    } else {
      // New focus
      setFocusCell({ row: rowIdx, col: colKey });
      setSelectionEnd(null);
    }
  }

  function handleCellDoubleClick(e: React.MouseEvent, rowIdx: number, colKey: string) {
    if (!gridEditMode) return;
    if (!editableCols.includes(colKey)) return;
    e.preventDefault();
    setFocusCell({ row: rowIdx, col: colKey });
    setSelectionEnd(null);
    enterEditMode({ row: rowIdx, col: colKey });
  }

  // ─── Copy handler (Ctrl+C) ───
  function handleCopy() {
    const rect = getSelectionRect();
    if (!rect) return;
    const lines: string[] = [];
    for (let r = rect.minRow; r <= rect.maxRow; r++) {
      const item = flatItems[r];
      if (!item) continue;
      const cells: string[] = [];
      for (let c = rect.minCol; c <= rect.maxCol; c++) {
        const colKey = editableCols[c];
        const col = initialColumns.find((c) => c.key === colKey);
        if (!col) continue;
        cells.push(getCellDisplayText(item, col));
      }
      lines.push(cells.join('\t'));
    }
    navigator.clipboard.writeText(lines.join('\n'));
  }

  // ─── Paste into cells handler ───
  // KEY BEHAVIOR: Copy 1 cell → select range → paste = fills ALL selected cells
  function handlePasteIntoCells(text: string) {
    if (!focusCell || !onCellEdit) return;
    const pastedLines = text.split(/\r?\n/).filter((l) => l.length > 0);
    if (pastedLines.length === 0) return;
    const pastedGrid = pastedLines.map((l) => (l.includes('\t') ? l.split('\t') : [l]));
    const isSingleValue = pastedGrid.length === 1 && pastedGrid[0].length === 1;

    const rect = getSelectionRect();
    if (!rect) return;
    const selRows = rect.maxRow - rect.minRow + 1;
    const selCols = rect.maxCol - rect.minCol + 1;
    const hasMultiSelection = selRows > 1 || selCols > 1;

    if (isSingleValue && hasMultiSelection) {
      // ★ FILL ALL SELECTED CELLS with the single copied value
      const value = pastedGrid[0][0].trim();
      for (let r = rect.minRow; r <= rect.maxRow; r++) {
        for (let c = rect.minCol; c <= rect.maxCol; c++) {
          applyValueToCell(r, c, value);
        }
      }
    } else if (isSingleValue) {
      // Pasting one value into one cell
      const startColIdx = editableCols.indexOf(focusCell.col);
      applyValueToCell(focusCell.row, startColIdx, pastedGrid[0][0].trim());
    } else {
      // Pasting multi-value: spread from focus cell, right then down
      const startColIdx = editableCols.indexOf(focusCell.col);
      if (startColIdx === -1) return;
      for (let ri = 0; ri < pastedGrid.length; ri++) {
        const rowIdx = focusCell.row + ri;
        if (rowIdx >= flatItems.length) break;
        for (let ci = 0; ci < pastedGrid[ri].length; ci++) {
          const colIdx = startColIdx + ci;
          if (colIdx >= editableCols.length) break;
          applyValueToCell(rowIdx, colIdx, pastedGrid[ri][ci].trim());
        }
      }
      // Extend selection to cover pasted area
      const endRow = Math.min(focusCell.row + pastedGrid.length - 1, flatItems.length - 1);
      const maxPastedCols = Math.max(...pastedGrid.map((r) => r.length));
      const endColIdx = Math.min(startColIdx + maxPastedCols - 1, editableCols.length - 1);
      setSelectionEnd({ row: endRow, col: editableCols[endColIdx] });
    }
  }

  // ─── Delete handler — clear selected cells ───
  function handleDeleteSelected() {
    const rect = getSelectionRect();
    if (!rect || !onCellEdit) return;
    for (let r = rect.minRow; r <= rect.maxRow; r++) {
      for (let c = rect.minCol; c <= rect.maxCol; c++) {
        applyValueToCell(r, c, '');
      }
    }
  }

  // ─── Drag-to-fill handle ───
  const fillDragRef = useRef<{ startRow: number; startCol: number; value: string } | null>(null);
  const [fillPreview, setFillPreview] = useState<number | null>(null); // target row for fill preview

  function handleFillMouseDown(e: React.MouseEvent) {
    if (!focusCell) return;
    e.preventDefault();
    e.stopPropagation();
    const col = initialColumns.find((c) => c.key === focusCell.col);
    if (!col) return;
    const item = flatItems[focusCell.row];
    const editKey = col.editKey || col.key;
    const value = getCellDisplayText(item, col);
    fillDragRef.current = {
      startRow: focusCell.row,
      startCol: editableCols.indexOf(focusCell.col),
      value,
    };

    const handleMove = (ev: MouseEvent) => {
      // Find which row the mouse is over
      const rows = tableRef.current?.querySelectorAll('tbody tr');
      if (!rows) return;
      let targetRow = focusCell.row;
      // Use the flatRowCounter data attributes to find the row
      rows.forEach((tr, _i) => {
        const rect = tr.getBoundingClientRect();
        if (ev.clientY >= rect.top && ev.clientY <= rect.bottom) {
          const idx = tr.getAttribute('data-flat-idx');
          if (idx != null) targetRow = parseInt(idx);
        }
      });
      setFillPreview(targetRow > focusCell.row ? targetRow : null);
    };
    const handleUp = (ev: MouseEvent) => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
      if (fillDragRef.current && fillPreview != null && fillPreview > focusCell.row) {
        // Fill the value from focusCell.row+1 to fillPreview
        const ci = fillDragRef.current.startCol;
        for (let r = focusCell.row + 1; r <= fillPreview; r++) {
          applyValueToCell(r, ci, fillDragRef.current.value);
        }
        setSelectionEnd({ row: fillPreview, col: focusCell.col });
      }
      fillDragRef.current = null;
      setFillPreview(null);
    };
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
  }

  // ─── Keyboard navigation ───
  const tableRef = useRef<HTMLDivElement>(null);

  function handleGridKeyDown(e: React.KeyboardEvent) {
    if (!gridEditMode) return;

    // If currently editing, don't intercept (let the input handle it)
    if (editingCell) return;

    if (!focusCell) return;

    // Copy (Ctrl+C)
    if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
      e.preventDefault();
      handleCopy();
      return;
    }

    // Paste (Ctrl+V)
    if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
      e.preventDefault();
      navigator.clipboard
        .readText()
        .then((text) => {
          if (text) handlePasteIntoCells(text);
        })
        .catch(() => {});
      return;
    }

    // Select all (Ctrl+A)
    if ((e.ctrlKey || e.metaKey) && e.key === 'a') {
      e.preventDefault();
      if (flatItems.length > 0 && editableCols.length > 0) {
        setFocusCell({ row: 0, col: editableCols[0] });
        setSelectionEnd({ row: flatItems.length - 1, col: editableCols[editableCols.length - 1] });
      }
      return;
    }

    // Delete / Backspace — clear selected cells
    if (e.key === 'Delete' || e.key === 'Backspace') {
      e.preventDefault();
      handleDeleteSelected();
      return;
    }

    // Enter or F2 — enter edit mode
    if (e.key === 'Enter' || e.key === 'F2') {
      e.preventDefault();
      enterEditMode(focusCell);
      return;
    }

    // Escape — clear selection
    if (e.key === 'Escape') {
      setFocusCell(null);
      setSelectionEnd(null);
      return;
    }

    // Arrow key navigation
    const ci = editableCols.indexOf(focusCell.col);
    if (ci === -1) return;

    if (
      e.key === 'ArrowUp' ||
      e.key === 'ArrowDown' ||
      e.key === 'ArrowLeft' ||
      e.key === 'ArrowRight'
    ) {
      e.preventDefault();
      if (e.shiftKey) {
        // Extend selection
        const end = selectionEnd || focusCell;
        const endCi = editableCols.indexOf(end.col);
        let er = end.row,
          ec = endCi;
        if (e.key === 'ArrowUp') er = Math.max(0, er - 1);
        else if (e.key === 'ArrowDown') er = Math.min(flatItems.length - 1, er + 1);
        else if (e.key === 'ArrowLeft') ec = Math.max(0, ec - 1);
        else if (e.key === 'ArrowRight') ec = Math.min(editableCols.length - 1, ec + 1);
        setSelectionEnd({ row: er, col: editableCols[ec] });
      } else {
        // Move focus
        let newRow = focusCell.row;
        let newCol = ci;
        if (e.key === 'ArrowUp') newRow = Math.max(0, newRow - 1);
        else if (e.key === 'ArrowDown') newRow = Math.min(flatItems.length - 1, newRow + 1);
        else if (e.key === 'ArrowLeft') newCol = Math.max(0, newCol - 1);
        else if (e.key === 'ArrowRight') newCol = Math.min(editableCols.length - 1, newCol + 1);
        setFocusCell({ row: newRow, col: editableCols[newCol] });
        setSelectionEnd(null);
      }
      return;
    }

    // Tab navigation
    if (e.key === 'Tab') {
      e.preventDefault();
      let newRow = focusCell.row;
      let newCol = ci;
      if (e.shiftKey) {
        newCol--;
        if (newCol < 0) {
          newCol = editableCols.length - 1;
          newRow = Math.max(0, newRow - 1);
        }
      } else {
        newCol++;
        if (newCol >= editableCols.length) {
          newCol = 0;
          newRow = Math.min(flatItems.length - 1, newRow + 1);
        }
      }
      setFocusCell({ row: newRow, col: editableCols[newCol] });
      setSelectionEnd(null);
      return;
    }

    // Type a character on a selected cell → enter edit mode with that char
    if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
      const col = initialColumns.find((c) => c.key === focusCell.col);
      if (col?.editable && (col.editType === 'text' || col.editType === 'date' || !col.editType)) {
        e.preventDefault();
        pendingChar.current = e.key;
        setEditingCell({ ...focusCell });
      }
    }
  }

  // ─── Paste from clipboard (original behavior for new rows) ───
  useEffect(() => {
    if (!onPaste && !onCellEdit) return;
    function handlePaste(e: ClipboardEvent) {
      if (
        !tableRef.current?.contains(document.activeElement) &&
        document.activeElement !== tableRef.current
      )
        return;
      const text = e.clipboardData?.getData('text/plain');
      if (!text) return;

      // If we have a focused cell in grid edit mode (and NOT editing), paste into cells
      if (gridEditMode && focusCell && !editingCell && onCellEdit) {
        e.preventDefault();
        handlePasteIntoCells(text);
        return;
      }

      // Otherwise, use the original append-rows behavior
      if (!onPaste) return;
      const lines = text.split(/\r?\n/).filter((l) => l.trim());
      if (lines.length === 0) return;
      const delim = lines[0].includes('\t') ? '\t' : ',';
      const editableColsList = orderedColumns.filter((c) => c.editable);
      if (editableColsList.length === 0) return;
      const parsed = lines.map((line) => {
        const cells = delim === '\t' ? line.split('\t') : parseCSVLine(line);
        const row: Record<string, string> = {};
        editableColsList.forEach((col, i) => {
          if (i < cells.length) row[col.editKey || col.key] = cells[i].trim();
        });
        return row;
      });
      e.preventDefault();
      onPaste!(parsed);
    }
    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [
    onPaste,
    onCellEdit,
    orderedColumns,
    gridEditMode,
    focusCell,
    editingCell,
    flatItems,
    editableCols,
  ]);

  // ─── Render: cell content in grid mode ───

  function renderGridCell(item: T, col: Column<T>, rowIdx: number) {
    const colKey = col.key;
    const isEditing = isCellEditing(rowIdx, colKey);
    const editKey = col.editKey || col.key;
    const rawVal = (item as any)[editKey];

    // Columns with custom renderEdit always show their editor in grid mode
    if (col.renderEdit) return col.renderEdit(item);

    // Non-editable columns show display value
    if (!col.editable || !onCellEdit) {
      return col.render ? col.render(item) : String(getColValue(item, col) || '\u2013');
    }

    // Checkbox: always shows the checkbox (no separate edit mode)
    if (col.editType === 'checkbox') {
      return (
        <input
          type="checkbox"
          checked={!!rawVal}
          onChange={(e) => {
            e.stopPropagation();
            onCellEdit!(item, editKey, e.target.checked);
          }}
          onClick={(e) => e.stopPropagation()}
          style={{ cursor: 'pointer' }}
        />
      );
    }

    // ── EDITING state: show input ──
    if (isEditing) {
      if (col.editType === 'select') {
        return (
          <ValidatedCombobox
            value={rawVal || ''}
            options={col.editOptions || []}
            onChange={(val) => onCellEdit!(item, editKey, val)}
            fontSize={fontSize}
            autoFocus
            onExit={(dir) => {
              if (dir === 'enter') exitEditMode('down');
              else if (dir === 'tab') exitEditMode('tab');
              else if (dir === 'shift-tab') exitEditMode('shift-tab');
              else if (dir === 'escape') exitEditMode('stay');
            }}
          />
        );
      }
      if (col.editType === 'date') {
        return (
          <input
            type="date"
            autoFocus
            defaultValue={pendingChar.current || rawVal || ''}
            ref={(el) => {
              if (el) {
                pendingChar.current = null;
              }
            }}
            onChange={(e) => onCellEdit!(item, editKey, e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                e.stopPropagation();
                exitEditMode('down');
              } else if (e.key === 'Tab') {
                e.preventDefault();
                e.stopPropagation();
                exitEditMode(e.shiftKey ? 'shift-tab' : 'tab');
              } else if (e.key === 'Escape') {
                e.preventDefault();
                e.stopPropagation();
                exitEditMode('stay');
              }
            }}
            onClick={(e) => e.stopPropagation()}
            onDoubleClick={(e) => e.stopPropagation()}
            style={{ ...gridInputStyle, fontSize: fontSize - 1 }}
          />
        );
      }
      // Default: text input
      const initValue = pendingChar.current != null ? pendingChar.current : rawVal || '';
      return (
        <input
          type="text"
          autoFocus
          defaultValue={initValue}
          ref={(el) => {
            if (el && pendingChar.current != null) {
              el.setSelectionRange(pendingChar.current.length, pendingChar.current.length);
              pendingChar.current = null;
            }
          }}
          onBlur={(e) => {
            const val = e.target.value;
            if (val !== (rawVal || '')) onCellEdit!(item, editKey, val);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              e.stopPropagation();
              const val = (e.target as HTMLInputElement).value;
              if (val !== (rawVal || '')) onCellEdit!(item, editKey, val);
              exitEditMode('down');
            } else if (e.key === 'Tab') {
              e.preventDefault();
              e.stopPropagation();
              const val = (e.target as HTMLInputElement).value;
              if (val !== (rawVal || '')) onCellEdit!(item, editKey, val);
              exitEditMode(e.shiftKey ? 'shift-tab' : 'tab');
            } else if (e.key === 'Escape') {
              e.preventDefault();
              e.stopPropagation();
              exitEditMode('stay');
            }
          }}
          onClick={(e) => e.stopPropagation()}
          onDoubleClick={(e) => e.stopPropagation()}
          style={{ ...gridInputStyle, fontSize }}
        />
      );
    }

    // ── SELECTED state: show display value (plain text, clickable) ──
    const displayText = getCellDisplayText(item, col);
    return (
      <div
        style={{
          width: '100%',
          minHeight: 20,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          cursor: 'cell',
        }}
      >
        {displayText || <span style={{ color: 'var(--tm)' }}>{'\u2013'}</span>}
      </div>
    );
  }

  // Track flat row index across groups during render
  let flatRowCounter = 0;

  // ─── Render ───
  return (
    <div
      ref={tableRef}
      tabIndex={0}
      style={{ outline: 'none', display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}
      onKeyDown={handleGridKeyDown}
      onMouseDown={() => {
        // If clicking empty space in the table container (not a cell), clear edit mode
      }}
    >
      {/* Toolbar */}
      <div
        style={{
          display: 'flex',
          gap: 8,
          alignItems: 'center',
          marginBottom: 12,
          flexWrap: 'wrap',
          flexShrink: 0,
        }}
      >
        <div style={{ position: 'relative', width: 200 }}>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={searchPlaceholder}
            style={{
              padding: '6px 10px',
              paddingRight: search ? 24 : 10,
              borderRadius: 5,
              border: '1px solid var(--bd)',
              background: 'var(--ib)',
              color: 'var(--tx)',
              fontSize: 12,
              width: '100%',
              boxSizing: 'border-box',
            }}
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              style={{
                position: 'absolute',
                right: 6,
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                color: 'var(--tm)',
                cursor: 'pointer',
                fontSize: 14,
                lineHeight: 1,
                padding: 0,
              }}
            >
              {'\u00d7'}
            </button>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={toolLabel}>Sort:</span>
          <div style={{ minWidth: 140 }}>
            <SearchableSelect
              value={sortKey}
              options={initialColumns
                .filter((c) => c.sortable !== false)
                .map((c) => ({ value: c.key, label: c.label }))}
              onChange={(v) => setSortKey(v)}
              searchPlaceholder="Search columns…"
              clearable={false}
              style={selectStyle}
            />
          </div>
          <button onClick={() => setSortAsc(!sortAsc)} style={{ ...pillBtn, width: 24 }}>
            {sortAsc ? '\u25B2' : '\u25BC'}
          </button>
        </div>

        {groupOptions && groupOptions.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={toolLabel}>Group:</span>
            <div style={{ minWidth: 140 }}>
              <SearchableSelect
                value={groupKey}
                options={[
                  { value: 'none', label: 'None' },
                  ...groupOptions.map((g) => ({ value: g.key, label: g.label })),
                ]}
                onChange={(v) => setGroupKey(v || 'none')}
                searchPlaceholder="Search groups…"
                clearable={false}
                style={selectStyle}
              />
            </div>
          </div>
        )}

        {/* Grid edit toggle */}
        {hasEditable && onCellEdit && (
          <button
            onClick={() => setGridEditMode(!gridEditMode)}
            style={{
              ...pillBtn,
              padding: '4px 10px',
              gap: 4,
              background: gridEditMode ? 'var(--ac)' : 'var(--sa)',
              color: gridEditMode ? 'var(--color-text-inverse)' : 'var(--tm)',
              borderRadius: 5,
              fontWeight: 600,
            }}
            title={gridEditMode ? 'Exit grid view' : 'Edit in grid view'}
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
            {gridEditMode ? 'Exit grid' : 'Edit in grid'}
          </button>
        )}

        {/* Selection info */}
        {gridEditMode &&
          focusCell &&
          (() => {
            const rect = getSelectionRect();
            if (!rect) return null;
            const rows = rect.maxRow - rect.minRow + 1;
            const cols = rect.maxCol - rect.minCol + 1;
            const total = rows * cols;
            if (total <= 1) return null;
            return (
              <span
                style={{
                  fontSize: 10,
                  color: 'var(--tm)',
                  padding: '3px 8px',
                  background: 'var(--sa)',
                  borderRadius: 4,
                }}
              >
                {rows}R x {cols}C selected
              </span>
            );
          })()}

        {/* Text size slider */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            marginLeft: 'auto',
          }}
        >
          <span style={{ fontSize: 10, color: 'var(--tm)' }}>Aa</span>
          <input
            type="range"
            min={10}
            max={18}
            value={fontSize}
            onChange={(e) => setFontSize(Number(e.target.value))}
            style={{ width: 60, cursor: 'pointer' }}
            title={`Text size: ${fontSize}px`}
          />
          <span style={{ fontSize: 10, color: 'var(--tm)', minWidth: 18, textAlign: 'right' }}>
            {fontSize}
          </span>
        </div>

        {toolbarExtra}
      </div>

      {/* Keyboard hints in grid mode */}
      {gridEditMode && (
        <div
          style={{
            fontSize: 10,
            color: 'var(--tm)',
            marginBottom: 8,
            display: 'flex',
            gap: 12,
            flexWrap: 'wrap',
            flexShrink: 0,
          }}
        >
          <span>Click to select</span>
          <span>Double-click or Enter to edit</span>
          <span>Shift+Click for range</span>
          <span>Ctrl+C/V copy/paste</span>
          <span>Arrows to navigate</span>
          <span>Del to clear</span>
          <span>Drag fill handle to copy down</span>
        </div>
      )}

      {/* Scrollable data area */}
      <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        {/* Data */}
        {filtered.length === 0 ? (
          <div
            style={{
              textAlign: 'center',
              padding: '50px 20px',
              background: 'var(--sf)',
              borderRadius: 10,
              border: '1px solid var(--bd)',
            }}
          >
            <div style={{ fontSize: 40, opacity: 0.12, marginBottom: 10 }}>{emptyIcon}</div>
            <h3 style={{ fontSize: fontSize + 2, margin: '0 0 6px', fontWeight: 600 }}>
              {data.length === 0 ? emptyTitle : 'No matches'}
            </h3>
            <p style={{ fontSize: fontSize - 1, color: 'var(--tm)', margin: 0 }}>
              {data.length === 0 ? emptyMessage : 'Try a different search.'}
            </p>
          </div>
        ) : (
          grouped.map((group, gi) => {
            if (gi === 0) flatRowCounter = 0;

            const isCollapsed =
              groupKey !== 'none' && group.label && collapsedGroups.has(group.label);

            return (
              <div key={gi} style={{ marginBottom: groupKey !== 'none' ? 20 : 0 }}>
                {groupKey !== 'none' && group.label && (
                  <div
                    onClick={() =>
                      setCollapsedGroups((prev) => {
                        const next = new Set(prev);
                        if (next.has(group.label)) next.delete(group.label);
                        else next.add(group.label);
                        return next;
                      })
                    }
                    style={{
                      fontSize,
                      fontWeight: 700,
                      color: 'var(--tx)',
                      marginBottom: isCollapsed ? 0 : 8,
                      paddingBottom: 4,
                      borderBottom: '1px solid var(--bd)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      cursor: 'pointer',
                      userSelect: 'none',
                    }}
                  >
                    <span
                      style={{
                        fontSize: fontSize - 2,
                        color: 'var(--tm)',
                        transition: 'transform .15s',
                        display: 'inline-block',
                        transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                      }}
                    >
                      {'\u25BE'}
                    </span>
                    {group.label}
                    <span style={{ fontSize: fontSize - 3, color: 'var(--tm)', fontWeight: 400 }}>
                      ({group.items.length})
                    </span>
                  </div>
                )}

                {isCollapsed ? (
                  ((): null => {
                    flatRowCounter += group.items.length;
                    return null;
                  })()
                ) : (
                  <div
                    style={{
                      background: 'var(--sf)',
                      borderRadius: 8,
                      border: '1px solid var(--bd)',
                      marginBottom: 4,
                    }}
                  >
                    <table
                      style={{
                        width: Math.max(
                          orderedColumns.reduce((s, c) => s + (columnWidths[c.key] || 150), 0) +
                            (actions ? 50 : 0),
                          0
                        ),
                        borderCollapse: 'collapse',
                        tableLayout: 'fixed',
                      }}
                    >
                      <colgroup>
                        {orderedColumns.map((col) => (
                          <col key={col.key} style={{ width: columnWidths[col.key] || 150 }} />
                        ))}
                        {actions && <col style={{ width: 50 }} />}
                      </colgroup>
                      <thead>
                        <tr>
                          {orderedColumns.map((col) => (
                            <th
                              key={col.key}
                              draggable={!isResizing.current}
                              onDragStart={(e) => {
                                if (isResizing.current) {
                                  e.preventDefault();
                                  return;
                                }
                                handleColDragStart(e, col.key);
                              }}
                              onDragOver={(e) => handleColDragOver(e, col.key)}
                              onDragLeave={() => setDragOverCol(null)}
                              onDrop={(e) => handleColDrop(e, col.key)}
                              onMouseDown={(e) => {
                                mouseDownTargetRef.current = e.currentTarget;
                              }}
                              onClick={(e) => {
                                if (
                                  mouseDownTargetRef.current === e.currentTarget &&
                                  col.sortable !== false
                                )
                                  toggleSort(col.key);
                                mouseDownTargetRef.current = null;
                              }}
                              onContextMenu={(e) => {
                                e.preventDefault();
                                setColMenuPos({ x: e.clientX, y: e.clientY });
                              }}
                              style={{
                                ...thStyle,
                                fontSize: Math.max(fontSize - 3, 9),
                                cursor: col.sortable !== false ? 'pointer' : 'default',
                                background:
                                  dragOverCol === col.key ? 'rgba(var(--acr),0.12)' : 'var(--sa)',
                                userSelect: 'none',
                              }}
                            >
                              <span
                                style={{
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {col.label}
                                {col.sortable !== false ? sortArrow(col.key) : ''}
                              </span>
                              <div
                                onMouseDown={(e) => handleResizeStart(e, col.key)}
                                onClick={(e) => e.stopPropagation()}
                                style={{
                                  position: 'absolute',
                                  right: 0,
                                  top: 0,
                                  bottom: 0,
                                  width: 5,
                                  cursor: 'col-resize',
                                  background: 'transparent',
                                }}
                                onMouseEnter={(e) =>
                                  (e.currentTarget.style.background = 'var(--ac)')
                                }
                                onMouseLeave={(e) =>
                                  (e.currentTarget.style.background = 'transparent')
                                }
                              />
                            </th>
                          ))}
                          {actions && (
                            <th
                              style={{ ...thStyle, width: 50, fontSize: Math.max(fontSize - 3, 9) }}
                            ></th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {group.items.map((item) => {
                          const rowId = String((item as any)[keyField]);
                          const rowIdx = flatRowCounter++;
                          return (
                            <tr
                              key={rowId}
                              data-flat-idx={rowIdx}
                              onMouseDown={(e) => {
                                mouseDownTargetRef.current = e.currentTarget;
                              }}
                              onClick={(e) => {
                                if (!gridEditMode && mouseDownTargetRef.current === e.currentTarget)
                                  onRowClick?.(item);
                                mouseDownTargetRef.current = null;
                              }}
                              style={{
                                cursor: gridEditMode
                                  ? 'default'
                                  : onRowClick
                                    ? 'pointer'
                                    : 'default',
                                transition: 'background .1s',
                              }}
                              onMouseEnter={(e) => {
                                if (!gridEditMode) e.currentTarget.style.background = 'var(--sa)';
                              }}
                              onMouseLeave={(e) => {
                                if (!gridEditMode) e.currentTarget.style.background = '';
                              }}
                            >
                              {orderedColumns.map((col) => {
                                const inSelection =
                                  gridEditMode && isCellInSelection(rowIdx, col.key);
                                const focused = gridEditMode && isCellFocused(rowIdx, col.key);
                                const editing = gridEditMode && isCellEditing(rowIdx, col.key);
                                const inFillPreview =
                                  gridEditMode &&
                                  fillPreview != null &&
                                  focusCell &&
                                  col.key === focusCell.col &&
                                  rowIdx > focusCell.row &&
                                  rowIdx <= fillPreview;

                                return (
                                  <td
                                    key={col.key}
                                    onMouseDown={(e) => handleCellMouseDown(e, rowIdx, col.key)}
                                    onDoubleClick={(e) => handleCellDoubleClick(e, rowIdx, col.key)}
                                    style={{
                                      ...tdStyle,
                                      fontSize,
                                      overflow: editing ? 'visible' : 'hidden',
                                      textOverflow: editing ? undefined : 'ellipsis',
                                      whiteSpace: editing ? undefined : 'nowrap',
                                      verticalAlign: 'top',
                                      padding: gridEditMode ? '4px 6px' : tdStyle.padding,
                                      position: 'relative',
                                      // Selection highlighting
                                      background: editing
                                        ? 'var(--sf)'
                                        : focused
                                          ? 'rgba(var(--acr),0.10)'
                                          : inSelection || inFillPreview
                                            ? 'rgba(var(--acr),0.05)'
                                            : undefined,
                                      outline: focused
                                        ? '2px solid var(--ac)'
                                        : inSelection
                                          ? '1px solid rgba(var(--acr),0.3)'
                                          : inFillPreview
                                            ? '1px dashed var(--ac)'
                                            : undefined,
                                      outlineOffset: focused
                                        ? '-2px'
                                        : inSelection || inFillPreview
                                          ? '-1px'
                                          : undefined,
                                      cursor: gridEditMode && col.editable ? 'cell' : undefined,
                                    }}
                                  >
                                    {gridEditMode
                                      ? renderGridCell(item, col, rowIdx)
                                      : col.render
                                        ? col.render(item)
                                        : String(getColValue(item, col) || '\u2013')}
                                    {/* Drag-to-fill handle */}
                                    {focused && !editing && col.editable && (
                                      <div
                                        onMouseDown={handleFillMouseDown}
                                        style={{
                                          position: 'absolute',
                                          bottom: -3,
                                          right: -3,
                                          width: 7,
                                          height: 7,
                                          background: 'var(--ac)',
                                          border: '1px solid var(--sf)',
                                          cursor: 'crosshair',
                                          zIndex: 10,
                                        }}
                                        title="Drag to fill"
                                      />
                                    )}
                                  </td>
                                );
                              })}
                              {actions && <td style={{ ...tdStyle, fontSize }}>{actions(item)}</td>}
                            </tr>
                          );
                        })}
                        {/* Blank "add" row at bottom — SharePoint-style */}
                        {onNewRow && gridEditMode && gi === grouped.length - 1 && (
                          <NewRowInput
                            columns={orderedColumns}
                            fontSize={fontSize}
                            onNewRow={onNewRow}
                          />
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
      {/* end scrollable data area */}

      {/* Column visibility context menu */}
      {colMenuPos && (
        <div
          style={{ position: 'fixed', inset: 0, zIndex: 9999 }}
          onClick={() => setColMenuPos(null)}
          onContextMenu={(e) => {
            e.preventDefault();
            setColMenuPos(null);
          }}
        >
          <div
            style={{
              position: 'absolute',
              left: colMenuPos.x,
              top: colMenuPos.y,
              background: 'var(--sf)',
              border: '1px solid var(--bd)',
              borderRadius: 8,
              boxShadow: '0 8px 24px rgba(0,0,0,.18)',
              padding: '6px 0',
              minWidth: 180,
              zIndex: 10000,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                padding: '4px 14px 6px',
                fontSize: 10,
                fontWeight: 700,
                color: 'var(--tm)',
                textTransform: 'uppercase',
                letterSpacing: '.04em',
              }}
            >
              Columns
            </div>
            {allOrderedColumns.map((col) => (
              <button
                key={col.key}
                onClick={() =>
                  setHiddenColumns((prev) => {
                    const next = new Set(prev);
                    if (next.has(col.key)) next.delete(col.key);
                    else next.add(col.key);
                    return next;
                  })
                }
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  width: '100%',
                  textAlign: 'left',
                  padding: '6px 14px',
                  fontSize: 12,
                  fontWeight: 500,
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'var(--tx)',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--sa)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
              >
                <span
                  style={{
                    width: 16,
                    textAlign: 'center',
                    fontSize: 13,
                    color: hiddenColumns.has(col.key) ? 'var(--bd)' : 'var(--ac)',
                  }}
                >
                  {hiddenColumns.has(col.key) ? '\u2610' : '\u2611'}
                </span>
                {col.label || col.key}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Blank "add" row component ───
function NewRowInput<T>({
  columns,
  fontSize,
  onNewRow,
}: {
  columns: Column<T>[];
  fontSize: number;
  onNewRow: (values: Record<string, string>) => void;
}) {
  const [values, setValues] = React.useState<Record<string, string>>({});
  const hasContent = Object.values(values).some((v) => v.trim());

  function submit() {
    if (!hasContent) return;
    onNewRow(values);
    setValues({});
  }

  return (
    <tr style={{ background: 'rgba(var(--acr),0.02)' }}>
      {columns.map((col, ci) => {
        const fk = col.editKey || col.key;
        if (!col.editable)
          return (
            <td key={col.key} style={{ ...tdStyle, fontSize, color: 'var(--tm)', opacity: 0.4 }}>
              {ci === 0 && !hasContent ? '+ New...' : ''}
            </td>
          );
        if (col.editType === 'select' && col.editOptions) {
          return (
            <td key={col.key} style={{ ...tdStyle, fontSize, padding: '4px 6px' }}>
              <SearchableSelect
                value={values[fk] || ''}
                options={col.editOptions.map((o) => ({ value: o.value, label: o.label }))}
                onChange={(v) => setValues((prev) => ({ ...prev, [fk]: v }))}
                searchPlaceholder="Search…"
                style={{
                  width: '100%',
                  padding: '3px 6px',
                  borderRadius: 3,
                  fontSize: fontSize - 1,
                }}
              />
            </td>
          );
        }
        return (
          <td key={col.key} style={{ ...tdStyle, fontSize, padding: '4px 6px' }}>
            <input
              value={values[fk] || ''}
              onChange={(e) => setValues((prev) => ({ ...prev, [fk]: e.target.value }))}
              onKeyDown={(e) => {
                if (e.key === 'Enter') submit();
              }}
              placeholder={ci === 0 ? '+ Add new...' : ''}
              style={{
                width: '100%',
                padding: '3px 6px',
                borderRadius: 3,
                border: '1px solid var(--bd)',
                background: 'var(--ib)',
                color: 'var(--tx)',
                fontSize: fontSize - 1,
                fontStyle: values[fk] ? 'normal' : 'italic',
              }}
            />
          </td>
        );
      })}
    </tr>
  );
}

// ─── CSV line parser (handles quoted fields) ───
function parseCSVLine(line: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"' && line[i + 1] === '"') {
        current += '"';
        i++;
      } else if (ch === '"') inQuotes = false;
      else current += ch;
    } else {
      if (ch === '"') inQuotes = true;
      else if (ch === ',') {
        result.push(current);
        current = '';
      } else current += ch;
    }
  }
  result.push(current);
  return result;
}

// ─── Styles ───

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '8px 12px',
  fontWeight: 700,
  color: 'var(--tm)',
  textTransform: 'uppercase',
  letterSpacing: '.04em',
  borderBottom: '1px solid var(--bd)',
  background: 'var(--sa)',
  position: 'sticky',
  top: 0,
  zIndex: 2,
};

const tdStyle: React.CSSProperties = {
  padding: '8px 12px',
  borderBottom: '1px solid var(--bd)',
};

const gridInputStyle: React.CSSProperties = {
  width: '100%',
  padding: '3px 6px',
  borderRadius: 3,
  border: '1px solid var(--bd)',
  background: 'var(--ib)',
  color: 'var(--tx)',
  outline: 'none',
  boxSizing: 'border-box',
};

const selectStyle: React.CSSProperties = {
  padding: '4px 8px',
  borderRadius: 4,
  border: '1px solid var(--bd)',
  background: 'var(--ib)',
  color: 'var(--tx)',
  fontSize: 11,
  cursor: 'pointer',
};

const pillBtn: React.CSSProperties = {
  padding: '4px 6px',
  borderRadius: 4,
  border: 'none',
  background: 'transparent',
  color: 'var(--tm)',
  cursor: 'pointer',
  fontSize: 11,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const toolLabel: React.CSSProperties = {
  fontSize: 11,
  color: 'var(--tm)',
  fontWeight: 600,
};
